import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useSelector } from 'ihp-bloom-redux/app/redux';
import { useUpdateParticipantTaskMutation } from 'ihp-bloom-redux/features/tasks/participantTaskApiSliceV3';
import { useGetParticipantGoalStatsQuery } from 'ihp-bloom-redux/features/tasks/taskApiSlice';

import { CenteredFullPageWrapper } from 'components/CenteredFullPageWrapper';
import ActivityInstructions from 'components/Activities/Instructions';
import { Loader } from 'components/Loader';
import CelebrationScreen from 'pages/Activities/CelebrationScreen';
import { convertUTCStringToLocalDateObject, dateFormatter } from 'utils/date';
import { articleScreensMap } from './utils';
import { taskTypeLabelMapping } from '../utils';
import {
  ArticlePageWrapper,
  StepperWrapper,
  StyledActivityHeader,
  StyledProgressButtons,
  ArticleContainer,
  ArticleDescription,
} from './styles';
import { ReactComponent as CheckIcon } from 'images/shared/check-1.svg';
import { useGetProfileParticipantTask } from '../hooks';
import { useMediaQuery } from '@mui/material';

const Article = () => {
  document.title = 'Single Ventricle SOURCE Article';
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const participantTaskId = parseInt(useParams().id);
  const navigate = useNavigate();
  const [benefitAttributes, setBenefitAttributes] = useState({});

  const { activeAccountProfile, person } = useSelector((state) => state.user);

  const accountProfileId = activeAccountProfile?.attributes?.profile?.id;

  const { refetch } = useGetParticipantGoalStatsQuery(person?.id, {
    skip: person?.id ? false : true,
  });

  const participantTaskDetails = useGetProfileParticipantTask(
    participantTaskId,
    accountProfileId
  );

  const [updateParticipantTaskMutation, { isLoading: isUpdatingTask }] =
    useUpdateParticipantTaskMutation();

  const handleFinish = async () => {
    const start_time = dateFormatter(
      convertUTCStringToLocalDateObject(participantTaskDetails?.startDate)
    );
    const end_time = dateFormatter(new Date());

    const requestBody = {
      participantTaskId: participantTaskId,
      payload: {
        data: {
          type: 'participant-tasks',
          id: String(participantTaskId),
          attributes: {
            status: 'completed',
            start_time,
            end_time,
          },
        },
      },
    };

    updateParticipantTaskMutation(requestBody)
      .then((response) => {
        refetch();
        if (participantTaskDetails?.attributes?.celebrationScreen?.display) {
          setBenefitAttributes(response?.data?.data?.attributes);
          setCurrentStep(currentStep + 1);
        } else {
          navigate('/my-activities');
        }
      })
      .catch(console.error);
  };

  const [currentStep, setCurrentStep] = useState(articleScreensMap.howTo);

  const hasHowTo = participantTaskDetails?.attributes?.howTo?.display;

  if (currentStep === 0 && !hasHowTo) {
    setCurrentStep(articleScreensMap.description);
  }

  const next = () => {
    if (currentStep === articleScreensMap.description) {
      handleFinish();
    } else if (currentStep === articleScreensMap.celebration) {
      navigate('/my-activities');
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const back = () => {
    if (
      currentStep - 1 === articleScreensMap.howTo &&
      !participantTaskDetails?.attributes?.howTo?.display
    ) {
      navigate('/my-activities');
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  let estimatedTime = 'N/A';
  if (participantTaskDetails?.estimatedTime) {
    estimatedTime = `${participantTaskDetails.estimatedTime} min`;
  }
  let timeLimit = undefined;
  if (participantTaskDetails?.timeLimit) {
    timeLimit = `${participantTaskDetails?.timeLimit} min`;
  }

  const stepsMap = {
    [articleScreensMap.howTo]: (
      <CenteredFullPageWrapper>
        <ActivityInstructions
          hideMetaData={isMobile}
          hideInstructionsTitle={!isMobile}
          title={participantTaskDetails?.title}
          type={taskTypeLabelMapping[participantTaskDetails?.type]}
          estimatedTime={estimatedTime}
          timeLimit={timeLimit}
          points={participantTaskDetails?.point}
          description={participantTaskDetails?.attributes?.howTo?.instructions}
          startButtonText='Continue'
          onClickContinue={next}
          onCancel={() => navigate('/my-activities')}
        />
      </CenteredFullPageWrapper>
    ),
    [articleScreensMap.description]: (
      <ArticleContainer>
        <ArticleDescription>
          <div
            dangerouslySetInnerHTML={{
              __html: participantTaskDetails?.attributes?.content,
            }}
          />
        </ArticleDescription>
      </ArticleContainer>
    ),
    [articleScreensMap.celebration]: (
      <CelebrationScreen
        attributes={{
          compensationValue: participantTaskDetails?.point || 10,
          thankyouMsg: participantTaskDetails?.task?.celebrationScreen?.title,
          description:
            participantTaskDetails?.attributes?.celebrationScreen?.description,
          ...benefitAttributes,
        }}
        maxHeight={false}
        sx={{ margin: 'auto' }}
      />
    ),
  };

  const getButtonLabel = () => {
    if (currentStep === articleScreensMap.celebration) {
      return 'Done';
    }
    if (
      currentStep === articleScreensMap.howTo ||
      currentStep === articleScreensMap.description
    ) {
      return 'Finish article';
    }
    return 'Continue';
  };

  const isCelebrationScreen = currentStep === articleScreensMap.celebration;
  const isCompleted = participantTaskDetails?.status === 'completed';

  if (isCompleted) {
    navigate('/my-activities');
  }

  if (participantTaskDetails?.loadingParticipantTask) {
    return <Loader />;
  }

  if (isUpdatingTask) {
    return <Loader />;
  }

  return (
    <ArticlePageWrapper>
      {!isCelebrationScreen && (
        <StyledActivityHeader
          title={participantTaskDetails?.title}
          points={participantTaskDetails?.point}
          type={taskTypeLabelMapping[participantTaskDetails?.type]}
          estimatedTime={estimatedTime}
          timeLimit={timeLimit}
          currentStep={currentStep}
          lastStep={2}
        />
      )}
      <StepperWrapper>
        {stepsMap[currentStep]}
        {currentStep > 0 && (
          <StyledProgressButtons
            isLastStep={currentStep === articleScreensMap.celebration}
            buttonProps={{
              startIcon: <CheckIcon />,
              label: getButtonLabel(),
              onClick: next,
            }}
            linkProps={{
              label: 'Back',
              disabled: currentStep === articleScreensMap.celebration,
              onClick: back,
              hide: currentStep === articleScreensMap.celebration,
            }}
          />
        )}
      </StepperWrapper>
    </ArticlePageWrapper>
  );
};

export default Article;
